import {
  fetchBusiness,
  fetchFilters,
  IDashboardFilter,
  IDateRangeKey,
  isBusinessBillingLocked,
  type IBusiness
} from '@query'
import dayjs from 'dayjs'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { QueryObserverResult, useQuery } from '@tanstack/react-query'
import { useBusinessContext } from './BusinessContext'
import { PermissionSet, usePermission, usePermissionsLoaded } from './PermissionContext'

export interface ICustomDateRange {
  dateFrom: string
  dateTo: string
}

interface Props {
  selectedRange: IDashboardFilter
  options: IDashboardFilter[]
  isReady: boolean
  isActive: boolean
  isDefaultRange: boolean
  reset: () => void
  showAll: () => void
  setRange: (range: IDashboardFilter) => void
  refetch: () => Promise<QueryObserverResult<IDashboardFilter[], unknown>>
}

export const DateRangeContext = React.createContext<Props>({} as Props)

export const useDateRangeContext = (): Props => {
  return useContext(DateRangeContext)
}

const buildCustomDateRange = (newRange: IDashboardFilter) => {
  const newDateFrom = dayjs(newRange.date_from, 'YYYY-MM-DD')
  const newDateTo = dayjs(newRange.date_to, 'YYYY-MM-DD')

  return {
    date_from: newDateFrom.isValid() ? newDateFrom.format('YYYY-MM-DD') : undefined,
    date_to: newDateTo.isValid() ? newDateTo.format('YYYY-MM-DD') : undefined
  }
}

export const DateRangeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { slug, data: business } = useBusinessContext()
  const canFetchDateRanges = usePermission(PermissionSet.DATE_RANGE_VIEW)
  const permissionsLoaded = usePermissionsLoaded()
  const isBillingLocked = isBusinessBillingLocked(business)
  const { isFetching: isBusinessFetching, isLoading: isBusinessLoading } = useQuery<IBusiness>({
    queryKey: [slug],
    queryFn: () => fetchBusiness({ slug }),
    enabled: Boolean(slug)
  })
  const isBusinessSettled = Boolean(slug) && !isBusinessLoading && !isBusinessFetching

  const initialValue = useMemo<IDashboardFilter>(
    () => ({
      key: 'period-this',
      date_from: dayjs().startOf('year').format('YYYY-MM-DD'),
      date_to: dayjs().endOf('year').format('YYYY-MM-DD')
    }),
    []
  )

  // Not persisted: a stale remembered range reads as "wrong numbers".
  const [range, setRange] = useState<IDashboardFilter>(initialValue)

  useEffect(() => {
    setRange(initialValue)
  }, [slug, initialValue])

  const validateAndSetRange = useCallback(
    (newRange: IDashboardFilter) => {
      setRange({
        ...newRange,
        ...(newRange.key === 'custom' ? buildCustomDateRange(newRange) : {})
      })
    },
    [setRange]
  )

  const {
    data: options_,
    isSuccess,
    refetch
  } = useQuery({
    queryKey: [slug, 'date_ranges'],
    queryFn: () => fetchFilters({ slug }),
    enabled: isBusinessSettled && !isBillingLocked && permissionsLoaded && canFetchDateRanges
  })

  const [option, options, defaultOption] = useMemo(() => {
    // Add custom range to the options
    const options = [
      ...(options_ || []),
      {
        key: 'custom' as IDateRangeKey,
        date_from: range?.date_from,
        date_to: range?.date_to
      }
    ]

    const defaultOption =
      options.find(option => option.key === 'period-this') ||
      options.find(option => option.key === 'year-this') ||
      options.find(option => option.key === 'all-history') ||
      initialValue

    const option = options.find(option => option.key === range?.key) || defaultOption || range

    return [option, options, defaultOption]
  }, [options_, range, initialValue])

  const reset = useCallback(() => {
    setRange(defaultOption)
  }, [defaultOption, setRange])

  const showAll = useCallback(() => {
    setRange(options.find(option => option.key === 'all-history') || defaultOption)
  }, [options, defaultOption, setRange])

  return (
    <DateRangeContext.Provider
      value={{
        selectedRange: option,
        options,
        reset,
        showAll,
        setRange: validateAndSetRange,
        // Skip filters API only after we know permissions and the user lacks bookkeeping view.
        isReady:
          isBillingLocked ||
          (permissionsLoaded && !canFetchDateRanges) ||
          (isSuccess && !!option?.key),
        // Default period still sends date_from/date_to; only all-history does not.
        isActive: Boolean(option?.key) && option?.key !== 'all-history',
        isDefaultRange: Boolean(option?.key) && option?.key === defaultOption?.key,
        refetch
      }}
    >
      {children}
    </DateRangeContext.Provider>
  )
}
